import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddyBottomNav } from "../components/CaddyBottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "#FAF7F0", card: "#FFFFFF", ink: "#2A1E5C", ink2: "#5D5A6B",
  muted: "#8B7E6A", line: "#E8E2D5", gold: "#F5CB5C", teal: "#2E86AB", green: "#3E8E4E",
};

const SECTIONS = [
  {
    id: "work",
    title: "About your work (optional)",
    fields: [
      { k: "work_type", q: "What kind of work do you do? (select any that apply)", type: "multi",
        opts: ["Freelancer / creative", "Gig or platform work", "Independent service provider", "Hands-on / trades", "Consultant", "Other"] },
    ],
  },
  {
    id: "clarity",
    title: "Clarity",
    fields: [
      { k: "clarity_before", q: "Before Payday Relay, how clear did your weekly income target feel?", type: "scale" },
      { k: "clarity_after", q: "After four relays, how clear does it feel now?", type: "scale" },
      { k: "relay_target_sense", q: "Did the relay target make sense?", type: "chips", opts: ["Yes", "Mostly", "No"] },
      { k: "daily_manageable", q: "Did the daily target make your work feel more manageable?", type: "chips", opts: ["Yes", "Somewhat", "No"] },
      { k: "payday_delay_sense", q: "Did the payday rhythm concept make sense?", type: "chips", opts: ["Yes", "Mostly", "No"] },
    ],
  },
  {
    id: "rhythm",
    title: "Behavior & rhythm",
    fields: [
      { k: "built_rhythm", q: "Did Payday Relay help you build a rhythm around earning?", type: "chips", opts: ["Yes", "A little", "No"] },
      { k: "done_enough", q: "Did it help you know when you had done enough for the week?", type: "chips", opts: ["Yes", "A little", "No"] },
      { k: "reduced_guilt", q: "Did it reduce work guilt, avoidance, or overworking?", type: "chips", opts: ["Yes", "A little", "No"] },
      { k: "closeout_compare", q: "Did the weekly closeout help you compare the plan to real life?", type: "chips", opts: ["Yes", "A little", "No"] },
      { k: "flexible_enough", q: "Did the system feel flexible enough for self-employed life?", type: "chips", opts: ["Yes", "Mostly", "No"] },
    ],
  },
  {
    id: "caddy",
    title: "Payday Relay",
    fields: [
      { k: "used_most", q: "What did you use most in Payday Relay?", type: "text" },
      { k: "felt_missing", q: "What felt missing?", type: "text" },
      { k: "felt_confusing", q: "What felt confusing?", type: "text" },
      { k: "matched_sheet", q: "Did the app match what you expected from the sheet?", type: "chips", opts: ["Yes", "Mostly", "No"] },
      { k: "broke_or_sync", q: "Did anything break or sync incorrectly?", type: "text" },
    ],
  },
  {
    id: "sheet",
    title: "The Sheet",
    fields: [
      { k: "sheet_made_sense", q: "Did the Payday Relay sheet make sense once it was created?", type: "chips", opts: ["Yes", "Mostly", "No"] },
      { k: "sheet_feel", q: "Did the sheet feel useful, overwhelming, or both?", type: "chips", opts: ["Useful", "Overwhelming", "Both"] },
      { k: "app_helped_sheet", q: "Did the app help make the sheet easier to use?", type: "chips", opts: ["Yes", "Somewhat", "No"] },
      { k: "numbers_wrong", q: "Did any numbers seem wrong?", type: "text" },
    ],
  },
  {
    id: "value",
    title: "Value",
    fields: [
      { k: "keep_using", q: "Would you keep using Payday Relay after beta?", type: "chips", opts: ["Yes", "Maybe", "No"] },
      { k: "worth_paying", q: "What would make it worth paying for?", type: "text" },
      { k: "fair_price", q: "What price would feel reasonable for Caddy after beta?", type: "text" },
      { k: "recommend", q: "Would you recommend this to another freelancer / self-employed person?", type: "chips", opts: ["Yes", "Maybe", "No"] },
      { k: "who_needs", q: "What kind of person do you think needs this most?", type: "text" },
    ],
  },
  {
    id: "open",
    title: "Open feedback",
    fields: [
      { k: "aha_moment", q: "What was your biggest \"aha\" moment?", type: "text" },
      { k: "frustrated", q: "What frustrated you?", type: "text" },
      { k: "fix_before_launch", q: "What should we fix before public launch?", type: "text" },
      { k: "describe_to_friend", q: "What language would you use to describe Payday Relay to a friend?", type: "text" },
      { k: "follow_up_ok", q: "Can we follow up with you for a short beta interview?", type: "chips", opts: ["Yes", "No"] },
    ],
  },
];

const Scale = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5 items-center">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="w-9 h-9 rounded-full border font-bold text-sm"
        style={value === n
          ? { background: COLOR.ink, color: "#fff", borderColor: COLOR.ink }
          : { background: "#fff", color: COLOR.ink2, borderColor: COLOR.line }}
        data-testid={`${testid}-${n}`}
      >
        {n}
      </button>
    ))}
    <span className="text-[10px] ml-1" style={{ color: COLOR.muted }}>1 = foggy · 5 = crystal clear</span>
  </div>
);

export default function BetaFeedbackSurveyPage() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | locked | form | testimonial | done | already
  const [closeouts, setCloseouts] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/feedback/prompt`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/beta-feedback", { replace: true }); return; }
        if (!res.ok) { setState("locked"); return; }
        const d = await res.json();
        setCloseouts(d.closeouts);
        if (d.closeouts < 4) setState("locked");
        else if (d.due !== "four_relay") setState("already");
        else setState("form");
      } catch {
        setState("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: "four_relay", answers, skipped: false }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Couldn't save, try again"); return; }
      toast.success("Beta survey saved, thank you 🏁");
      const positive = answers.keep_using === "Yes" || answers.recommend === "Yes";
      setState(positive ? "testimonial" : "done");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const chooseTestimonial = async (choice) => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/caddy/feedback/testimonial`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice }),
      });
      setState("done");
    } finally {
      setBusy(false);
    }
  };

  if (state === "error") return <CaddyErrorCard />;

  return (
    <div className="min-h-screen pb-24" style={{ background: COLOR.bg }}>
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <Link to="/app" className="text-xs underline" style={{ color: COLOR.muted }} data-testid="survey-back-link">← Back to the Caddy</Link>

        {state === "loading" && <p className="mt-10 text-sm" style={{ color: COLOR.muted }}>Loading…</p>}

        {state === "locked" && (
          <div className="mt-10 rounded-2xl p-6 border text-center" style={{ background: COLOR.card, borderColor: COLOR.line }} data-testid="survey-locked">
            <h1 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Not quite yet</h1>
            <p className="text-sm" style={{ color: COLOR.ink2 }}>
              The full beta survey opens after your <b>4th relay closeout</b>, you've closed {closeouts} so far.
              Four relays gives you enough time to actually feel the rhythm before we ask about it.
            </p>
          </div>
        )}

        {state === "already" && (
          <div className="mt-10 rounded-2xl p-6 border text-center" style={{ background: COLOR.card, borderColor: COLOR.line }} data-testid="survey-already">
            <h1 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Already in, thank you</h1>
            <p className="text-sm" style={{ color: COLOR.ink2 }}>We've got your beta survey. It's shaping what ships next.</p>
          </div>
        )}

        {state === "form" && (
          <>
            <p className="text-[10px] font-bold uppercase tracking-widest mt-6 mb-1" style={{ color: "#7A5E10" }}>🏆 Four Relay Challenge complete</p>
            <h1 className="text-3xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="survey-title">
              You completed the Four Relay Challenge. We'd love your beta feedback.
            </h1>
            <p className="text-sm mb-8" style={{ color: COLOR.ink2 }}>
              Everything is optional, answer what you have energy for. ~3 minutes.
            </p>

            {SECTIONS.map((s) => (
              <div key={s.id} className="rounded-2xl p-5 border mb-4" style={{ background: COLOR.card, borderColor: COLOR.line }} data-testid={`survey-section-${s.id}`}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: COLOR.teal }}>{s.title}</p>
                <div className="space-y-4">
                  {s.fields.map((f) => (
                    <div key={f.k}>
                      <p className="text-sm mb-1.5" style={{ color: COLOR.ink }}>{f.q}</p>
                      {f.type === "scale" && <Scale value={answers[f.k]} onChange={(v) => set(f.k, v)} testid={`survey-${f.k}`} />}
                      {f.type === "chips" && (
                        <div className="flex flex-wrap gap-1.5">
                          {f.opts.map((o) => (
                            <button
                              key={o}
                              type="button"
                              onClick={() => set(f.k, o)}
                              className="text-xs px-3.5 py-1.5 rounded-full border font-semibold"
                              style={answers[f.k] === o
                                ? { background: COLOR.ink, color: "#fff", borderColor: COLOR.ink }
                                : { background: "#fff", color: COLOR.ink2, borderColor: COLOR.line }}
                              data-testid={`survey-${f.k}-${o.toLowerCase()}`}
                            >
                              {o}
                            </button>
                          ))}
                        </div>
                      )}
                      {f.type === "multi" && (
                        <div className="flex flex-wrap gap-1.5">
                          {f.opts.map((o) => {
                            const sel = Array.isArray(answers[f.k]) && answers[f.k].includes(o);
                            return (
                              <button
                                key={o}
                                type="button"
                                onClick={() => set(f.k, sel ? answers[f.k].filter((x) => x !== o) : [...(answers[f.k] || []), o])}
                                className="text-xs px-3.5 py-1.5 rounded-full border font-semibold"
                                style={sel
                                  ? { background: COLOR.ink, color: "#fff", borderColor: COLOR.ink }
                                  : { background: "#fff", color: COLOR.ink2, borderColor: COLOR.line }}
                                data-testid={`survey-${f.k}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              >
                                {o}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {f.type === "text" && (
                        <textarea
                          rows={2}
                          value={answers[f.k] || ""}
                          onChange={(e) => set(f.k, e.target.value)}
                          placeholder="Optional…"
                          className="w-full px-3 py-2 rounded-xl border text-sm focus:outline-none"
                          style={{ borderColor: COLOR.line, background: COLOR.bg, color: COLOR.ink }}
                          data-testid={`survey-${f.k}-input`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <button
              onClick={submit}
              disabled={busy || Object.keys(answers).length === 0}
              className="w-full py-3.5 rounded-full font-bold text-base disabled:opacity-40 transition-transform hover:scale-[1.01] mb-3"
              style={{ background: COLOR.gold, color: COLOR.ink }}
              data-testid="survey-submit-btn"
            >
              {busy ? "Sending…" : "Send my beta feedback →"}
            </button>
            <p className="text-[11px] text-center mb-6" style={{ color: COLOR.muted }}>
              Your answers are tied to your account so our team can follow up. Never published without your permission.
            </p>
          </>
        )}

        {state === "testimonial" && (
          <div className="mt-10 rounded-2xl p-6 border text-center" style={{ background: "#FFF8E1", borderColor: COLOR.gold }} data-testid="survey-testimonial">
            <h2 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Would you be open to sharing a short testimonial after beta?
            </h2>
            <p className="text-xs mb-5" style={{ color: COLOR.ink2 }}>Nothing gets published without your explicit permission.</p>
            <div className="space-y-2 max-w-xs mx-auto">
              <button onClick={() => chooseTestimonial("yes")} disabled={busy} className="w-full py-2.5 rounded-full font-bold text-sm" style={{ background: COLOR.ink, color: "#fff" }} data-testid="testimonial-yes">Yes</button>
              <button onClick={() => chooseTestimonial("maybe_later")} disabled={busy} className="w-full py-2.5 rounded-full font-bold text-sm border" style={{ borderColor: COLOR.line, color: COLOR.ink, background: "#fff" }} data-testid="testimonial-maybe">Maybe later</button>
              <button onClick={() => chooseTestimonial("no")} disabled={busy} className="w-full py-2 text-xs underline" style={{ color: COLOR.muted }} data-testid="testimonial-no">No thanks</button>
            </div>
          </div>
        )}

        {state === "done" && (
          <div className="mt-10 rounded-2xl p-6 border text-center" style={{ background: COLOR.card, borderColor: COLOR.line }} data-testid="survey-done">
            <h2 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Thank you, racer</h2>
            <p className="text-sm mb-5" style={{ color: COLOR.ink2 }}>Your four-relay feedback is exactly what shapes the public launch.</p>
            <Link to="/app" className="inline-block px-6 py-2.5 rounded-full font-bold text-sm" style={{ background: COLOR.gold, color: COLOR.ink }} data-testid="survey-done-back">
              Back to the Caddy →
            </Link>
          </div>
        )}
      </div>
      <CaddyBottomNav active="more" />
    </div>
  );
}
